import React, { useState, useEffect, useCallback } from "react";
import { Drawer, Table, Tag, Typography, Empty } from "antd";
import { getSubscriptionCriticalChanges } from "../api/subscriptions/subscriptionsRequest";
import { getInfractionCriticalChanges } from "../api/infractions/infractionRequest";

const { Text } = Typography;

const formatDateTime = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Map the stored model class (e.g. "App\\Models\\Payment") to a label + color.
const modelMeta = (model) => {
  const short = String(model || "").split("\\").pop();
  const map = {
    Subscription: { label: "Abonnement", color: "purple" },
    Echeance: { label: "Échéance", color: "geekblue" },
    Payment: { label: "Paiement", color: "green" },
    PaymentDetail: { label: "Détail paiement", color: "cyan" },
    Transaction: { label: "Transaction", color: "blue" },
    Agent: { label: "Agent (solde)", color: "gold" },
    Infraction: { label: "Infraction", color: "volcano" },
    Warning: { label: "Avertissement", color: "orange" },
  };
  return map[short] || { label: short || "—", color: "default" };
};

/**
 * Per-entity audit history. Aggregates every critical change recorded across all
 * records related to the selected subscription or infraction. Superadmin-gated by caller.
 */
const AuditHistoryDrawer = ({ open, onClose, entity, entityId, title }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!entity || !entityId) return;
    setLoading(true);
    try {
      const fn = entity === "infraction" ? getInfractionCriticalChanges : getSubscriptionCriticalChanges;
      const resp = await fn(entityId);
      setRows(Array.isArray(resp) ? resp : resp?.data || []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [entity, entityId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const columns = [
    { title: "Date", dataIndex: "created_at", width: 130, render: formatDateTime },
    {
      title: "Élément",
      dataIndex: "model",
      width: 130,
      render: (m, r) => {
        const meta = modelMeta(m);
        return (
          <span>
            <Tag color={meta.color}>{meta.label}</Tag>
            {r.model_id ? <Text type="secondary" style={{ fontSize: 11 }}>#{r.model_id}</Text> : null}
          </span>
        );
      },
    },
    { title: "Champ", dataIndex: "field", width: 110, render: (v) => <Tag>{v}</Tag> },
    {
      title: "Ancienne → Nouvelle",
      render: (_, r) => (
        <span>
          <Text delete type="secondary">{r.old_value ?? "—"}</Text>
          {" → "}
          <Text strong>{r.new_value ?? "—"}</Text>
        </span>
      ),
    },
    { title: "Par", dataIndex: "changed_by_name", width: 130, render: (v, r) => v || r.changed_by || "—" },
    { title: "Raison", dataIndex: "reason", render: (v) => v || "—" },
  ];

  return (
    <Drawer title={title || "Historique des modifications"} open={open} onClose={onClose} width={920}>
      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ pageSize: 20, hideOnSinglePage: true, showTotal: (t) => `${t} modification(s)` }}
        locale={{ emptyText: <Empty description="Aucune modification enregistrée" /> }}
      />
    </Drawer>
  );
};

export default AuditHistoryDrawer;
